import { Trans, useTranslation } from 'react-i18next';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';
import { BidiText } from '../common';
import disclosureConfig from '../../../shared-config/content_processing_disclosure.config.json';

interface ConsentCheckboxesProps {
  termsAccepted: boolean;
  contentProcessingConsent: boolean;
  onTermsChange: (checked: boolean) => void;
  onContentProcessingChange: (checked: boolean) => void;
  disabled?: boolean;
  termsError?: string;
  contentProcessingError?: string;
}

const PRIVACY_URL = 'https://www.panluma.ai/privacy-policy';
const TERMS_URL = 'https://www.panluma.ai/terms-of-service';
const SUBPROCESSORS_URL = 'https://www.panluma.ai/security/sub-processors';

/**
 * The disclosure groups, ordered as configured.
 *
 * Source of truth is `shared/config/content_processing_disclosure.config.json`
 * (reached through the `src/shared-config` symlink), NOT this file — that config
 * is what `tools/check_subprocessor_coverage.py` invariant E forces every declared
 * sub-processor to be classified against, so a newly-engaged content processor
 * cannot ship without either appearing in this box or being explicitly exempted.
 * Adding a group here means adding an `auth:consent.group.<id>` key in every locale.
 */
const DISCLOSURE_GROUPS = [...disclosureConfig.groups].sort((a, b) => a.sort_order - b.sort_order);

/**
 * Two affirmative-consent checkboxes required to register. The disclosure block
 * above the second checkbox names the third-party providers and the data
 * categories sent — Apple App Review 5.1.1(i)/5.1.2(i) requires this information
 * to be visible during registration, not deferred to the Privacy Policy alone.
 *
 * Grouped by PURPOSE rather than one line per vendor: the vendor set churns while
 * the purposes are stable, so the box stays the same height as providers are
 * added, and every provider is still named at the point of consent.
 */
export function ConsentCheckboxes({
  termsAccepted,
  contentProcessingConsent,
  onTermsChange,
  onContentProcessingChange,
  disabled = false,
  termsError,
  contentProcessingError,
}: ConsentCheckboxesProps) {
  return (
    <div className="space-y-3 pt-1">
      <div className="flex items-start gap-2.5">
        <Checkbox
          id="terms_accepted"
          checked={termsAccepted}
          onCheckedChange={(checked) => onTermsChange(checked === true)}
          disabled={disabled}
          className="mt-0.5"
          data-testid="terms-checkbox"
        />
        <div className="flex-1">
          <Label htmlFor="terms_accepted" className="text-sm font-normal leading-snug cursor-pointer">
            <Trans
              i18nKey="auth:consent.termsLabel"
              components={{
                privacyLink: <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground" />,
                termsLink: <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground" />,
              }}
            />
          </Label>
          {termsError && <p className="text-xs text-destructive mt-1">{termsError}</p>}
        </div>
      </div>

      <DataProcessingDisclosure />

      <div className="flex items-start gap-2.5">
        <Checkbox
          id="content_processing_consent"
          checked={contentProcessingConsent}
          onCheckedChange={(checked) => onContentProcessingChange(checked === true)}
          disabled={disabled}
          className="mt-0.5"
          data-testid="content-processing-checkbox"
        />
        <div className="flex-1">
          <Label htmlFor="content_processing_consent" className="text-sm font-normal leading-snug cursor-pointer">
            <Trans i18nKey="auth:consent.contentProcessingLabel" />
          </Label>
          {contentProcessingError && <p className="text-xs text-destructive mt-1">{contentProcessingError}</p>}
        </div>
      </div>
    </div>
  );
}

function DataProcessingDisclosure() {
  const { t } = useTranslation('auth');

  // Locale-owned list separator (", " in en/es/he, "、" in zh) rather than a
  // hardcoded ", ". `Intl.ListFormat` would be the richer answer but is ES2021,
  // and this project's tsconfig `lib` is ES2020 — not worth widening the whole
  // project's ambient API surface for one enumeration.
  const separator = t('consent.providerSeparator');

  return (
    <div className="rounded-md border border-border bg-muted/40 p-3 text-sm" data-testid="data-processing-disclosure">
      <p className="font-medium text-foreground">{t('consent.disclosureTitle')}</p>
      <p className="mt-1.5 text-muted-foreground">{t('consent.disclosureIntro')}</p>
      {/* A definition list, not bullets: each row is a purpose (the term the user
          is deciding about) and the providers that serve it (the value). */}
      <dl className="mt-2 space-y-1 text-muted-foreground">
        {DISCLOSURE_GROUPS.map((group) => (
          <div key={group.id} className="leading-snug">
            <dt className="inline font-medium text-foreground">{t(`consent.group.${group.id}`)}</dt>
            <dd className="inline">
              {' — '}
              <BidiText>{group.providers.map((provider) => provider.label).join(separator)}</BidiText>
            </dd>
          </div>
        ))}
      </dl>
      <p className="mt-2.5 text-xs text-muted-foreground leading-snug">
        <Trans
          i18nKey="auth:consent.disclosureFooter"
          components={{
            privacyLink: <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground" />,
            subprocessorLink: <a href={SUBPROCESSORS_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground" />,
          }}
        />
      </p>
    </div>
  );
}
